import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Phone,
  ArrowLeft,
  Building2,
  MapPin,
  Mail,
  Globe,
  FileText,
  CheckCircle,
  Loader2,
  Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { getAllStates } from '@/data/stateData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const industries = [
  { id: "hvac", name: "HVAC & Heating" },
  { id: "plumbing", name: "Plumbing" },
  { id: "electrical", name: "Electrical" },
  { id: "home_repair", name: "Home Repair & Remodeling" },
  { id: "roofing", name: "Roofing" },
  { id: "landscaping", name: "Landscaping" },
  { id: "auto_repair", name: "Auto Repair" },
  { id: "septic", name: "Septic Services" },
  { id: "dental", name: "Dental & Medical" },
  { id: "restaurant", name: "Restaurant" },
  { id: "fishing_charter", name: "Fishing Charter" },
  { id: "sightseeing", name: "Sightseeing Tours" },
  { id: "gym", name: "Gym & Fitness" }
];

export default function ContractorRegister() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const states = getAllStates();
  
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    industry: '',
    state: '',
    city: '',
    phone: '',
    email: '',
    website: '',
    description: '',
    services: '',
    years_in_business: '',
    license_number: '',
    agree_to_terms: false,
    sms_consent: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agree_to_terms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }
    
    if (!formData.sms_consent) {
      toast.error('Please consent to SMS communications to use our service');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const submitData = {
        ...formData,
        services: formData.services.split(',').map(s => s.trim()).filter(s => s),
        years_in_business: formData.years_in_business ? parseInt(formData.years_in_business) : null
      };

      await axios.post(`${BACKEND_URL}/api/contractors/register`, submitData);
      
      setSubmitted(true);
      toast.success('Registration successful!');
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg w-full bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center"
        >
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Registration Submitted!</h1>
          <p className="text-slate-400 mb-6">
            Thank you for registering. Your listing will be reviewed and activated within 24-48 hours.
            We'll send you an email confirmation once your profile is live.
          </p>
          
          {/* Premium Upgrade CTA */}
          <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Users className="w-5 h-5 text-yellow-400" />
              <span className="font-semibold text-yellow-400">Want More Visibility?</span>
            </div>
            <p className="text-slate-300 text-sm mb-4">
              Upgrade to Premium for <span className="text-white font-bold">$49/month</span> and get:
            </p>
            <ul className="text-left text-sm space-y-2 mb-4">
              <li className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                Featured placement at the top of search results
              </li>
              <li className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                Upload your company logo
              </li>
              <li className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                Respond to customer reviews
              </li>
              <li className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                Premium badge on your listing
              </li>
            </ul>
            <Button
              onClick={() => navigate('/contractors/premium')}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-semibold"
            >
              Upgrade to Premium
            </Button>
          </div>
          
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/us-demo')}
              variant="outline"
              className="w-full border-slate-700 hover:bg-slate-800"
            >
              Explore the Map
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="w-full text-slate-400 hover:text-white"
            >
              Back to Home
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/us-demo')}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg">Contractor Registration</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Hero */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-500/10 border border-sky-500/20 mb-4">
              <Users className="w-4 h-4 text-sky-400" />
              <span className="text-sm text-sky-400 font-medium">Join Our Network</span>
            </div>
            <h1 className="text-3xl font-bold mb-3">List Your Business</h1>
            <p className="text-slate-400">
              Get discovered by customers looking for services in your area.
              Registration is free and your listing will be active within 48 hours.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Business Info */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h2 className="font-semibold mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-sky-400" />
                Business Information
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Business Name *</label>
                  <Input
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleChange}
                    placeholder="Your Company Name"
                    required
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Industry *</label>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      required
                      className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white"
                    >
                      <option value="">Select Industry</option>
                      {industries.map(ind => (
                        <option key={ind.id} value={ind.id}>{ind.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Years in Business</label>
                    <Input
                      name="years_in_business"
                      type="number"
                      min="0"
                      value={formData.years_in_business}
                      onChange={handleChange}
                      placeholder="e.g., 10"
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-1">Services Offered</label>
                  <Input
                    name="services"
                    value={formData.services}
                    onChange={handleChange}
                    placeholder="e.g., Furnace Repair, AC Installation, Maintenance"
                    className="bg-slate-800 border-slate-700"
                  />
                  <p className="text-xs text-slate-500 mt-1">Separate services with commas</p>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Tell customers about your business..."
                    rows={3}
                    className="w-full px-3 py-2 rounded-md bg-slate-800 border border-slate-700 text-white resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-1">License Number</label>
                  <Input
                    name="license_number"
                    value={formData.license_number}
                    onChange={handleChange}
                    placeholder="Your business license (optional)"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h2 className="font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-sky-400" />
                Location
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">State *</label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                    className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white"
                  >
                    <option value="">Select State</option>
                    {states.map(state => (
                      <option key={state.abbr} value={state.abbr}>{state.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">City *</label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Your city"
                    required
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h2 className="font-semibold mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-sky-400" />
                Contact Information
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Contact Name *</label>
                  <Input
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Phone *</label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      required
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Email *</label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      required
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-1">Website</label>
                  <Input
                    name="website"
                    type="url"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://yourwebsite.com"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
            </div>

            {/* Terms */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                name="agree_to_terms"
                checked={formData.agree_to_terms}
                onChange={handleChange}
                className="mt-1"
                id="terms"
              />
              <label htmlFor="terms" className="text-sm text-slate-400">
                I agree to the <a href="/terms" className="text-sky-400 hover:underline">Terms of Service</a> and 
                <a href="/privacy" className="text-sky-400 hover:underline"> Privacy Policy</a>. 
                I understand my listing will be reviewed before going live.
              </label>
            </div>
            
            {/* SMS Consent */}
            <div className="flex items-start gap-3 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <input
                type="checkbox"
                name="sms_consent"
                checked={formData.sms_consent}
                onChange={handleChange}
                className="mt-1"
                id="sms_consent"
                data-testid="sms-consent-checkbox"
              />
              <label htmlFor="sms_consent" className="text-sm text-slate-400">
                <span className="text-white font-medium">SMS Communications Consent:</span> I agree to receive SMS messages from Alaska AI Services including appointment confirmations, reminders, lead notifications, and service updates. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe at any time. Reply HELP for assistance.
              </label>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 py-6 text-lg"
              data-testid="contractor-register-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Registration'
              )}
            </Button>
          </form>
        </motion.div>
      </main>
    </div>
  );
}
